import axios from 'axios'

/**
 * VAPT #6 (Possible CSRF) — CSRF token handling for admin cookie-authenticated writes.
 *
 * The admin protected chain (admin.cloud.whilter.ai) uses a stateless double-submit token: an
 * `XSRF-TOKEN` cookie plus a matching `X-XSRF-TOKEN` header on state-changing requests. This SPA
 * runs on a foreign domain (dev.whilter.ai) and cannot read admin's httpOnly cookie, so it learns
 * the token from `GET {admin}/admin/csrf` and echoes it in the header; the browser still sends the
 * cookie and the server compares the two.
 *
 * Only requests to the admin origin get the header. `REACT_APP_SCHEMA_URL`, `REACT_APP_CACHE_URL`
 * and `REACT_APP_HPI_URL` all resolve to admin; the sibling services (playground, studio-api,
 * api-dev) neither issue nor validate this token and must not receive it.
 *
 * Server enforcement is flag-gated (`CSRF_ENFORCE`), so sending the header is harmless before the
 * flip — which is what makes the FE/BE rollout independent. See charp-2.0 SecurityConfig / #6.
 */

const CSRF_HEADER = 'X-XSRF-TOKEN'
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

let cachedToken = null
let inFlight = null

/** Admin origin (no trailing slash). SCHEMA/CACHE/HPI all point here; siblings do not. */
function getAdminBase() {
  const base = process.env.REACT_APP_SCHEMA_URL || ''
  return base.replace(/\/+$/, '')
}

export function isMutating(method) {
  return MUTATING_METHODS.has(String(method || 'GET').toUpperCase())
}

/** True only for absolute URLs on the admin origin (and only when that origin is configured). */
export function isAdminUrl(url) {
  const base = getAdminBase()
  return !!base && typeof url === 'string' && url.startsWith(base)
}

export function getCachedCsrfToken() {
  return cachedToken
}

/** Forget the cached token (call on logout / to isolate tests). */
export function clearCsrfToken() {
  cachedToken = null
  inFlight = null
}

/**
 * Fetch (or return the cached) CSRF token from `{admin}/admin/csrf`. Concurrent callers share one
 * in-flight request. Uses the bare default axios so it never recurses through the interceptors that
 * call it. Returns null on failure (e.g. not authenticated) — callers then send no header, which is
 * harmless while enforcement is off and recovered by the 403 retry once it is on.
 */
export function ensureCsrfToken(force = false) {
  if (cachedToken && !force) return Promise.resolve(cachedToken)
  if (inFlight && !force) return inFlight

  const base = getAdminBase()
  if (!base) return Promise.resolve(null)

  const request = axios
    .get(`${base}/admin/csrf`, {
      withCredentials: true,
      headers: { Accept: 'application/json' },
    })
    .then(response => {
      const token = response && response.data && response.data.token
      cachedToken = typeof token === 'string' ? token : null
      return cachedToken
    })
    .catch(() => null)
    .finally(() => {
      inFlight = null
    })

  inFlight = request
  return request
}

/**
 * Attach the CSRF header to a mutating admin request config. Awaits the token bootstrap on the
 * first such call per session (cached thereafter). No-op for reads and non-admin hosts.
 */
export async function attachCsrfToken(config) {
  if (!isMutating(config.method) || !isAdminUrl(config.url)) return config
  const token = await ensureCsrfToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers[CSRF_HEADER] = token
  }
  return config
}

/**
 * Install the CSRF request/response interceptors on an axios client (the shared `instance` folds
 * this into its existing interceptors; this covers the default `axios` client used by the plain
 * `axios.*` admin writes in the metadata/library crud modules). Idempotent per client.
 *
 * On a 403 for a mutating admin request it force-refreshes the token once, re-attaches it, and
 * retries a single time — covering a cold start before the token was warmed or a rotated token.
 */
export function installCsrfInterceptors(client) {
  if (client.__csrfInstalled) return
  client.__csrfInstalled = true

  client.interceptors.request.use(
    config => attachCsrfToken(config),
    error => Promise.reject(error)
  )

  client.interceptors.response.use(
    response => response,
    async error => {
      const status = error && error.response && error.response.status
      const config = error && error.config
      if (
        status === 403 &&
        config &&
        !config.__csrfRetried &&
        isMutating(config.method) &&
        isAdminUrl(config.url)
      ) {
        config.__csrfRetried = true
        const token = await ensureCsrfToken(true)
        if (token) {
          config.headers = config.headers || {}
          config.headers[CSRF_HEADER] = token
          return client(config)
        }
      }
      return Promise.reject(error)
    }
  )
}

export { CSRF_HEADER }
